import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { BehaviorSubject, EMPTY, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { DsButtonIntent, DsButtonVariants, DsDropdownComponent, Icons } from '@levelaccess/design-system';
import { AiChatService } from '../ai-chat.service';
import { AiChatDeleteService } from '../ai-chat-delete.service';
import { AiChatRenameService } from '../ai-chat-rename.service';
import { AiChatStateService } from '../ai-chat-state.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationPosition } from '../../../models/notification.model';
import { TranslateService } from '../../../translate/translate.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { IChatSession, IChatSessionPage, MAX_PINNED_CHAT_SESSIONS } from '../../../../../shared/interfaces/ai-chat.interface';
import { errorMessagesNames } from '../../../../../shared/constants/errors';

const PAGE_SIZE = 15;

@Component({
  selector: 'app-ai-chat-history',
  templateUrl: './ai-chat-history.component.html',
  styleUrls: ['./ai-chat-history.component.scss'],
  standalone: false,
})
export class AiChatHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isExpanded: boolean = false;
  @Input() public activeSessionId: string | undefined;
  @Output() public sessionSelected: EventEmitter<IChatSession> = new EventEmitter<IChatSession>();
  @Output() public sessionDeleted: EventEmitter<string> = new EventEmitter<string>();

  @ViewChildren('sessionButton') private sessionButtons!: QueryList<ElementRef>;
  @ViewChildren('dropdown') private sessionDropdowns!: QueryList<DsDropdownComponent>;
  @ViewChild('pagination', { read: ElementRef }) private paginationEl!: ElementRef;

  private subscriptions: Subscription;
  private pendingPaginationFocus: 'prev' | 'next' | null = null;
  private pendingFocusAfterDeletion: number | null = null;
  private tabEnteredFromOutside: boolean = false;
  private readonly queryState: BehaviorSubject<{ search: string; page: number }> = new BehaviorSubject<{
    search: string;
    page: number;
  }>({ search: '', page: 1 });

  protected get currentSearch(): string {
    return this.queryState.value.search;
  }

  protected readonly PAGE_SIZE: number = PAGE_SIZE;

  public sessions: IChatSession[] = [];
  public totalSessions: number = 0;
  public isLoading: boolean = true;
  public currentPage: number = 1;

  protected readonly Icons: typeof Icons = Icons;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly DsButtonIntent: typeof DsButtonIntent = DsButtonIntent;

  constructor(
    private elementRef: ElementRef,
    private aiChatService: AiChatService,
    private aiChatDeleteService: AiChatDeleteService,
    private aiChatRenameService: AiChatRenameService,
    private aiChatStateService: AiChatStateService,
    private featureFlagService: FeatureFlagService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {
    this.subscriptions = new Subscription();
  }

  protected get isRenameEnabled(): boolean {
    return this.featureFlagService.variation(FeatureFlagCollection.aiChatRenameChatSession, false);
  }

  public ngOnInit(): void {
    const initialPage = this.activeSessionId ? this.aiChatStateService.getSelectedChatPage() : 1;
    this.currentPage = initialPage;
    if (initialPage !== 1) {
      this.queryState.next({ search: '', page: initialPage });
    }

    this.subscriptions.add(
      this.queryState
        .pipe(
          tap(() => (this.isLoading = true)),
          switchMap(({ search, page }: { search: string; page: number }) =>
            this.aiChatService.getChatHistory(page, PAGE_SIZE, search || undefined).pipe(
              catchError((): typeof EMPTY => {
                this.isLoading = false;
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe((result: IChatSessionPage): void => {
          this.sessions = result.sessions;
          this.totalSessions = result.total;
          this.isLoading = false;
          this.focusPaginationButtonAfterPageChange();
          this.focusAfterDeletion();
        }),
    );

    this.subscriptions.add(
      this.aiChatService.sessionTitleUpdated$.subscribe(({ sessionId, title }: { sessionId: string; title: string }): void => {
        const sessionInList = this.sessions.some((s: IChatSession) => s.sessionId === sessionId);
        if (sessionInList) {
          this.sessions = this.sessions.map((s: IChatSession) =>
            s.sessionId === sessionId ? { ...s, title, titleTruncated: false } : s,
          );
        } else {
          this.queryState.next(this.queryState.value);
        }
      }),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const activeSessionIdChange = changes['activeSessionId'];
    if (!activeSessionIdChange || activeSessionIdChange.isFirstChange()) return;

    const page = this.activeSessionId ? this.aiChatStateService.getSelectedChatPage() : 1;
    if (page !== this.currentPage) {
      this.currentPage = page;
      this.queryState.next({ search: this.queryState.value.search, page });
    }
  }

  public refresh(): void {
    this.queryState.next(this.queryState.value);
  }

  public onSearch(query: string): void {
    this.currentPage = 1;
    this.queryState.next({ search: query, page: 1 });
  }

  public onPageChange(page: number): void {
    this.pendingPaginationFocus = page < this.currentPage ? 'prev' : 'next';
    this.currentPage = page;
    this.queryState.next({ search: this.queryState.value.search, page });
  }

  protected get hasPinned(): boolean {
    return this.sessions.some((s: IChatSession) => s.isFavorite);
  }

  protected get pinnedSessions(): IChatSession[] {
    return this.sessions
      .filter((s: IChatSession) => s.isFavorite)
      .sort((a: IChatSession, b: IChatSession) => {
        const aTime = a.favoritedAt ? new Date(a.favoritedAt).getTime() : 0;
        const bTime = b.favoritedAt ? new Date(b.favoritedAt).getTime() : 0;
        return bTime - aTime;
      });
  }

  protected get unpinnedSessions(): IChatSession[] {
    return this.sessions.filter((s: IChatSession) => !s.isFavorite);
  }

  private get renderedSessions(): IChatSession[] {
    return [...this.pinnedSessions, ...this.unpinnedSessions];
  }

  public onSelectSession(session: IChatSession): void {
    this.aiChatStateService.setSelectedChatPage(this.currentPage);
    this.sessionSelected.emit(session);
  }

  @HostListener('document:keydown', ['$event'])
  public onTabKeydownHandler(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;

    this.tabEnteredFromOutside = !this.elementRef.nativeElement.contains(document.activeElement);
    setTimeout((): void => {
      this.tabEnteredFromOutside = false;
    });
  }

  @HostListener('focusin')
  public onFocusInHandler(): void {
    if (!this.tabEnteredFromOutside) return;
    this.tabEnteredFromOutside = false;
    this.focusActiveSessionButton();
  }

  @HostListener('keydown.arrowUp', ['$event'])
  public onArrowUpKeydownHandler(event: KeyboardEvent): void {
    this.focusAdjacentSessionButton(event, -1);
  }

  @HostListener('keydown.arrowDown', ['$event'])
  public onArrowDownKeydownHandler(event: KeyboardEvent): void {
    this.focusAdjacentSessionButton(event, 1);
  }

  public onPinSession(session: IChatSession): void {
    const newValue = !session.isFavorite;
    const sessionIndexBeforePin = this.renderedSessions.findIndex((s: IChatSession) => s.sessionId === session.sessionId);

    const favoritedAt = newValue ? new Date().toISOString() : undefined;
    this.sessions = this.sessions.map((s: IChatSession) =>
      s.sessionId === session.sessionId ? { ...s, isFavorite: newValue, favoritedAt } : s,
    );
    this.subscriptions.add(
      this.aiChatService.favoriteSession(session.sessionId, newValue).subscribe({
        next: (): void => {
          this.notificationService.success(
            this.translateService.instant(newValue ? 'chat_pinned_successfully' : 'chat_unpinned_successfully'),
            NotificationPosition.Toast,
            undefined,
            undefined,
            undefined,
            undefined,
            (): void => {
              const idx = this.renderedSessions.findIndex((s: IChatSession) => s.sessionId === session.sessionId);
              if (idx >= 0) {
                this.sessionDropdowns.toArray()[idx]?.dropdownToggleBtn?.elRef?.nativeElement?.focus();
              } else {
                const dropdowns = this.sessionDropdowns.toArray();
                const fallbackIdx = Math.min(sessionIndexBeforePin, dropdowns.length - 1);
                if (fallbackIdx >= 0) {
                  dropdowns[fallbackIdx]?.dropdownToggleBtn?.elRef?.nativeElement?.focus();
                }
              }
            },
          );
          this.queryState.next(this.queryState.value);
        },
        error: (err: { error?: { app?: { name?: string } } } | null): void => {
          this.sessions = this.sessions.map((s: IChatSession) =>
            s.sessionId === session.sessionId ? { ...s, isFavorite: session.isFavorite, favoritedAt: session.favoritedAt } : s,
          );
          if (err?.error?.app?.name === errorMessagesNames.AiChatPinLimitReached) {
            this.notificationService.warn(
              this.translateService.instant('chat_pin_limit_reached', [MAX_PINNED_CHAT_SESSIONS]),
              NotificationPosition.Toast,
            );
          } else {
            this.notificationService.error(
              this.translateService.instant('unexpected_error_try_again'),
              NotificationPosition.Toast,
            );
          }
        },
      }),
    );
  }

  public onDeleteSession(sessionId: string, toggleEl?: HTMLElement): void {
    const sessionIndex = this.renderedSessions.findIndex((s: IChatSession) => s.sessionId === sessionId);

    this.subscriptions.add(
      this.aiChatDeleteService.confirmAndDeleteSession(sessionId, toggleEl).subscribe({
        next: (): void => {
          const isLastOnPage = this.sessions.length === 1;
          const newPage = isLastOnPage && this.currentPage > 1 ? this.currentPage - 1 : this.currentPage;
          this.currentPage = newPage;
          this.pendingFocusAfterDeletion = sessionIndex;
          this.queryState.next({ search: this.queryState.value.search, page: newPage });
          this.sessionDeleted.emit(sessionId);
        },
      }),
    );
  }

  public onRenameSession(sessionId: string, toggleEl?: HTMLElement): void {
    const session = this.sessions.find((s: IChatSession) => s.sessionId === sessionId);

    this.subscriptions.add(
      this.aiChatRenameService.confirmAndRenameSession(sessionId, session?.title ?? '', toggleEl).subscribe(),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private focusAdjacentSessionButton(event: KeyboardEvent, offset: number): void {
    const buttons = this.sessionButtons.toArray().map((ref: ElementRef) => ref.nativeElement as HTMLElement);
    const activeIndex = buttons.indexOf(document.activeElement as HTMLElement);
    if (activeIndex === -1) return;

    event.preventDefault();
    buttons[activeIndex + offset]?.focus();
  }

  private focusActiveSessionButton(): void {
    const targetIndex = this.renderedSessions.findIndex((s: IChatSession) => s.sessionId === this.activeSessionId);
    if (targetIndex === -1) return;

    (this.sessionButtons.toArray()[targetIndex]?.nativeElement as HTMLElement)?.focus();
  }

  private focusAfterDeletion(): void {
    if (this.pendingFocusAfterDeletion === null) return;
    const deletedIndex = this.pendingFocusAfterDeletion;
    this.pendingFocusAfterDeletion = null;
    setTimeout((): void => {
      const buttons = this.sessionButtons.toArray();
      const targetIndex = Math.min(deletedIndex, buttons.length - 1);
      if (targetIndex >= 0) {
        (buttons[targetIndex]?.nativeElement as HTMLElement)?.focus();
      }
    });
  }

  private focusPaginationButtonAfterPageChange(): void {
    if (!this.pendingPaginationFocus) return;
    const direction = this.pendingPaginationFocus;
    this.pendingPaginationFocus = null;
    setTimeout((): void => {
      // ds-pagination does not expose its prev/next buttons — query by their stable internal class names
      // see https://levelaccess.atlassian.net/browse/DS-1908
      const selector = direction === 'prev' ? '.ds-pagination-prev-button' : '.ds-pagination-next-button';
      (this.paginationEl?.nativeElement?.querySelector(selector) as HTMLElement | null)?.focus();
    });
  }
}
